import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { IMAGE_SRC } from '../../../shared-constants';
import { HeaderSearchComponent } from './header-search/header-search.component';
import { DISPLAY_CONSTS } from './table-header.constants';

@Component({
  selector: 'app-table-header',
  templateUrl: './table-header.component.html',
  styleUrls: ['./table-header.component.scss'],
  standalone: false,
})
/**
 * TableHeaderComponent
 *
 * A configurable header bar commonly used above data tables. It can display:
 * - Header text
 * - Search bar (emits search text and clear events)
 * - Clear filter button
 * - Export to Excel button
 * - Add item actions (supports primary and alternate styles)
 * - Results counter
 * - Optional radio selection group
 * - Layout/padding customization
 *
 * The component emits user interactions for the parent to handle
 * (e.g., filtering a MatTableDataSource or opening dialogs).
 *
 * Responsibilities:
 * - UI-only: renders controls and emits events.
 * - Delegates clearing the search input field to the child HeaderSearchComponent.
 *
 * Contracts:
 * - (searchEvent): emits the current string when user types/commits search.
 * - (searchClear): emitted when user clears search from header.
 * - (clearFilterEvent): emitted when clear filter button is clicked.
 * - (exportExcelEvent): emitted on export click.
 * - (addItemEvent): emitted with a boolean payload when add button is clicked.
 * - (exportRadioButtonChange): emits the native event when radio changes.
 */
export class TableHeaderComponent {
  /**
   * Reference to the child search component.
   * Used to imperatively clear the search input UI.
   *
   * Template should have: #searchHeader on the HeaderSearchComponent instance
   */
  @ViewChild('searchHeader') searchHeader!: HeaderSearchComponent;

  /** When true, shows an alternate header variant (styling/layout). */
  @Input() hasAltHeader = false;

  /** Shortcut to hide all actionable buttons in the header. */
  @Input() hasNoButtons = false;

  // ===== Header text =====

  /** Text displayed as the header title. */
  @Input() headerText!: string;

  /** Toggles the visibility of the header title. */
  @Input() hasHeader = true;

  // ===== Showing results =====

  /** Number of results to display next to the header (e.g., "Showing 25 results"). */
  @Input() numResults = 0;

  /** Toggles visibility of the "Showing results" text. */
  @Input() hasShowingResultsText = false;

  // ===== Clear filter =====

  /** If true, renders a "Clear Filter" control. */
  @Input() hasFilter = true;

  /** Disables the "Clear Filter" control when true. */
  @Input() filterDisabled = false;

  /** Emitted when user clicks "Clear Filter". */
  @Output() clearFilterEvent = new EventEmitter<void>();

  // ===== Export to Excel =====

  /** If true, renders an "Export Excel" control. */
  @Input() hasExcel = true;

  /** Disables the "Export Excel" control when true. */
  @Input() excelDisabled = false;

  /** Emitted when user clicks "Export Excel". */
  @Output() exportExcelEvent = new EventEmitter<void>();

  // ===== Add new item =====

  /**
   * Use alternate add item style (e.g., different icon or layout).
   * When true, shows alternative action button instead of default.
   */
  @Input() hasAlternateAddItem = false;

  /** Use alternate add item style with a single, consolidated button. */
  @Input() hasSingleAlternateAddItem = false;

  /** Icon/image source for the alternate add item action. */
  @Input() alternateAddItemSrc = IMAGE_SRC.OPERATIONS_STOP;

  /** Text label for the alternate add button. */
  @Input() alternateAddButtonText = DISPLAY_CONSTS.ADD_STOP;

  /** Text label for the default add button. */
  @Input() addButtonText = DISPLAY_CONSTS.ADD_DELIVERY;

  /** Toggles visibility of the default add item button. */
  @Input() hasAddItem = true;

  /** Disables the add item button when true. */
  @Input() addItemDisabled = false;

  /**
   * Emitted when user clicks the add item action.
   * Payload can indicate which add variant to use if needed.
   */
  @Output() addItemEvent = new EventEmitter<boolean>();

  // ===== Search bar =====

  /** Toggles visibility of the search bar. */
  @Input() hasSearchBar = false;

  /** Disables the search input when true. */
  @Input() searchBarDisabled = false;

  /**
   * Emitted whenever the user types/commits text into the search input.
   * Payload: the current search string.
   */
  @Output() searchEvent = new EventEmitter<string>();

  /**
   * Emitted when the search is cleared from the header UI.
   * Parent should clear the datasource filter and optionally reset paginator.
   */
  @Output() searchClear = new EventEmitter<void>();

  // ===== Radio button group =====

  /** Value for the first radio option. */
  @Input() firstOptionValue!: string;

  /** Label for the first radio option. */
  @Input() firstOptionName!: string;

  /** Value for the second radio option. */
  @Input() secondOptionValue!: string;

  /** Label for the second radio option. */
  @Input() secondOptionName!: string;

  /** Header text for the radio group. */
  @Input() radioHeader!: string;

  /** Toggles visibility of the radio group. */
  @Input() hasRadioButton = false;

  /** Disables the radio group when true. */
  @Input() radioButtonDisabled = false;

  /** Two-way bound model for the radio selection (if parent uses it). */
  @Input() radioModel!: string;

  /**
   * Emitted when the radio selection changes.
   * Payload: native DOM Event from the change.
   */
  @Output() exportRadioButtonChange = new EventEmitter<Event>();

  // ===== Layout / padding =====

  /** Right padding units (used by the template to adjust spacing). */
  @Input() rightPadding = 5;

  /** When true, applies compact/collapsed padding styles. */
  @Input() hasCollapsedPadding = true;

  /** Display constants accessible from the template. */
  readonly DISP_CONSTANTS = DISPLAY_CONSTS;

  /** Image sources accessible from the template. */
  readonly IMAGES = IMAGE_SRC;

  // ===== Methods: emitters and helpers =====

  /**
   * Emits the add item event.
   * @param $event - boolean to indicate which add flow (alternate vs default) if applicable.
   */
  sendAddItemSignal($event: boolean): void {
    this.addItemEvent.emit($event);
  }

  /** Emits the clear filter event. Parent should reset its filter state. */
  sendFilterSignal(): void {
    this.clearFilterEvent.emit();
  }

  /** Emits the export Excel event. Parent should perform the export. */
  sendExportExcelSignal(): void {
    this.exportExcelEvent.emit();
  }

  /**
   * Emits the current search text when user types/commits input.
   * The template should pass the native input event here.
   *
   * @param $event - native input event from the search control
   */
  sendSearchSignal($event: Event): void {
    this.searchEvent.emit(($event?.target as HTMLInputElement).value);
  }

  /**
   * Emits the "search clear" event for the parent to reset datasource filter.
   * Does **not** clear the input UI by itself—see `clearSearchText()` for UI clearing.
   */
  sendSearchClear(): void {
    this.searchClear.emit();
  }

  /**
   * Imperatively clears the search input UI by delegating to the child
   * HeaderSearchComponent. Useful when the parent wants to clear the visible
   * field (e.g., after an operation or dialog).
   */
  clearSearchText(): void {
    this.searchHeader?.clearSearch();
  }

  /**
   * Forwards the radio button change event to the parent.
   * @param $event - native change event from the radio group
   */
  sendRadioButtonChange($event: Event): void {
    this.exportRadioButtonChange.emit($event);
  }
}
